import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import DataTable from './DataTable';
import FormModal from './FormModal';
import { getHotels, createHotel, updateHotel, deleteHotel, getDestinations } from '../../../services/travelInventoryService';

const HotelsManager = ({ searchQuery }) => {
  const [hotels, setHotels] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [hotelsData, destinationsData] = await Promise.all([
        getHotels(),
        getDestinations(),
      ]);
      setHotels(hotelsData || []);
      setDestinations(destinationsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this hotel?')) return;
    
    try {
      await deleteHotel(id);
      await loadData();
    } catch (error) {
      console.error('Error deleting hotel:', error);
      alert('Failed to delete hotel');
    }
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingItem) {
        await updateHotel(editingItem?.id, formData);
      } else {
        await createHotel(formData);
      }
      setIsModalOpen(false);
      await loadData();
    } catch (error) {
      console.error('Error saving hotel:', error);
      throw error;
    }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'accommodation_type', label: 'Type', sortable: true },
    { key: 'star_rating', label: 'Stars', sortable: true },
    { key: 'price_per_night', label: 'Price/Night', type: 'currency' },
    { key: 'average_rating', label: 'Rating', sortable: true },
  ];

  const formFields = [
    {
      name: 'destination_id',
      label: 'Destination',
      type: 'select',
      required: true,
      options: destinations?.map((d) => ({ value: d?.id, label: `${d?.name}, ${d?.country}` })),
    },
    { name: 'name', label: 'Hotel Name', type: 'text', required: true },
    {
      name: 'accommodation_type',
      label: 'Type',
      type: 'select',
      required: true,
      options: [
        { value: 'hotel', label: 'Hotel' },
        { value: 'resort', label: 'Resort' },
        { value: 'hostel', label: 'Hostel' },
        { value: 'apartment', label: 'Apartment' },
        { value: 'villa', label: 'Villa' },
        { value: 'guesthouse', label: 'Guesthouse' },
        { value: 'boutique', label: 'Boutique' },
      ],
    },
    { name: 'description', label: 'Description', type: 'textarea', required: true },
    { name: 'short_description', label: 'Short Description', type: 'textarea' },
    { name: 'address', label: 'Address', type: 'text' },
    { name: 'city', label: 'City', type: 'text' },
    { name: 'country', label: 'Country', type: 'text' },
    { name: 'postal_code', label: 'Postal Code', type: 'text' },
    { name: 'latitude', label: 'Latitude', type: 'number', step: '0.0000001' },
    { name: 'longitude', label: 'Longitude', type: 'number', step: '0.0000001' },
    { name: 'star_rating', label: 'Star Rating', type: 'rating', required: true },
    { name: 'price_per_night', label: 'Price per Night', type: 'number', step: '0.01', required: true },
    { name: 'currency', label: 'Currency', type: 'text', defaultValue: 'PHP' },
    {
      name: 'price_range',
      label: 'Price Range',
      type: 'select',
      options: [
        { value: 'budget', label: 'Budget' },
        { value: 'moderate', label: 'Moderate' },
        { value: 'expensive', label: 'Expensive' },
        { value: 'luxury', label: 'Luxury' },
      ],
    },
    { name: 'image_url', label: 'Hotel Image', type: 'image-upload' },
    { name: 'image_alt', label: 'Image Alt Text', type: 'text' },
    { name: 'phone', label: 'Phone', type: 'text' },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'website', label: 'Website', type: 'text' },
    { name: 'check_in_time', label: 'Check-in Time', type: 'text', defaultValue: '14:00' },
    { name: 'check_out_time', label: 'Check-out Time', type: 'text', defaultValue: '12:00' },
    { name: 'cancellation_policy', label: 'Cancellation Policy', type: 'textarea' },
    { name: 'total_rooms', label: 'Total Rooms', type: 'number' },
    {
      name: 'amenities',
      label: 'Amenities',
      type: 'checkbox-group',
      options: [
        { value: 'wifi', label: 'Free WiFi' },
        { value: 'parking', label: 'Free Parking' },
        { value: 'pool', label: 'Swimming Pool' },
        { value: 'gym', label: 'Fitness Center' },
        { value: 'spa', label: 'Spa' },
        { value: 'restaurant', label: 'Restaurant' },
        { value: 'bar', label: 'Bar' },
        { value: 'room_service', label: 'Room Service' },
        { value: 'airport_shuttle', label: 'Airport Shuttle' },
        { value: 'pet_friendly', label: 'Pet Friendly' },
        { value: 'air_conditioning', label: 'Air Conditioning' },
        { value: 'laundry', label: 'Laundry Service' },
      ],
    },
    { name: 'is_featured', label: 'Featured', type: 'checkbox' },
  ];

  const filteredData = hotels?.filter((item) => {
    const matchesSearch = searchQuery
      ? item?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      : true;
    const matchesFilter = filterType === 'all' || item?.accommodation_type === filterType;
    return matchesSearch && matchesFilter;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Hotels & Accommodations</h2>
          <p className="text-gray-600 mt-1">Manage hotels, resorts, and lodging options</p>
        </div>
        <Button iconName="Plus" onClick={handleCreate}>
          Add Hotel
        </Button>
      </div>

      <div className="mb-4">
        <Select
          value={filterType}
          onChange={setFilterType}
          options={[
            { value: 'all', label: 'All Types' },
            { value: 'hotel', label: 'Hotel' },
            { value: 'resort', label: 'Resort' },
            { value: 'hostel', label: 'Hostel' },
            { value: 'villa', label: 'Villa' },
          ]}
          className="max-w-xs"
        />
      </div>

      <DataTable
        data={filteredData}
        columns={columns}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <FormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        title={editingItem ? 'Edit Hotel' : 'Add Hotel'}
        fields={formFields}
        initialData={editingItem}
      />
    </div>
  );
};

export default HotelsManager;